import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";
import { generateClient } from "aws-amplify/data";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "userPool",
});

class InstitutionManagementService {
  private async getAuthInfo() {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    const token = idToken?.toString();

    return { idToken, token };
  }

  private extractDomain(emailOrUrl: string): string {
    if (emailOrUrl.includes("@")) {
      return emailOrUrl.split("@")[1].toLowerCase().trim();
    }

    let domain = emailOrUrl.toLowerCase().trim();

    domain = domain.replace(/^https?:\/\//, "");
    domain = domain.replace(/^www\./, "");
    domain = domain.replace(/\/$/, "");

    domain = domain.split("/")[0];

    return domain;
  }

  async getAllInstitutions(): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.Institution.list({
        authMode: "userPool",
        authToken: token,
      });

      if (errors) {
        console.error("Error fetching institutions.");
        throw new Error("Failed to fetch institutions");
      }

      return data;
    } catch (error) {
      console.error("Error fetching institutions:", error);
      throw error;
    }
  }

  async getInstitutionById(
    institutionId: string,
    institutionName?: string,
  ): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();
      const authMode = token ? "userPool" : "iam";

      if (!institutionName) {
        const { data: allInstitutions } = await client.models.Institution.list({
          authMode: authMode as any,
          authToken: token,
        });

        const institution = allInstitutions?.find(
          (inst) => inst.institutionId === institutionId,
        );

        if (!institution) {
          throw new Error("Institution not found");
        }

        return institution;
      }

      const { data, errors } = await client.models.Institution.get(
        { institutionId, institutionName },
        {
          authMode: authMode as any,
          authToken: token,
        },
      );

      if (errors) {
        console.error("Error fetching institution.");
        throw new Error("Failed to fetch institution");
      }

      return data;
    } catch (error) {
      console.error("Error fetching institution:", error);
      throw error;
    }
  }

  async getInstitutionAgents(institutionId: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.Agent.list({
        filter: {
          institutionId: {
            contains: institutionId,
          },
        },
        authMode: "userPool",
        authToken: token,
      });

      if (errors) {
        console.error("Error fetching institution agents.");
        throw new Error("Failed to fetch institution agents");
      }

      return data;
    } catch (error) {
      console.error("Error fetching institution agents:", error);
      throw error;
    }
  }

  async assignAgentsToInstitution(
    agentIds: string | string[],
    institutionId: string,
  ): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();
      const agentIdArray = Array.isArray(agentIds) ? agentIds : [agentIds];

      const { data: allInstitutions, errors } =
        await client.models.Institution.list({
          authMode: "userPool",
          authToken: token,
        });

      if (errors) {
        throw new Error("Failed to fetch institutions");
      }

      const institution = allInstitutions?.find(
        (inst) => inst.institutionId === institutionId,
      );

      if (!institution) {
        throw new Error("Institution not found");
      }

      const currentAgentIds = (institution.agentId || []).filter(
        (id): id is string => id !== null,
      );

      const newAgentIds = agentIdArray.filter(
        (id) => !currentAgentIds.includes(id),
      );

      if (newAgentIds.length === 0) {
        return { success: true, message: "All agents already assigned" };
      }

      for (const agentId of newAgentIds) {
        const { data: agent } = await client.models.Agent.get(
          { agentId },
          { authMode: "userPool", authToken: token },
        );

        if (agent) {
          const updatedAgentInstitutionIds = agent.institutionId
            ? [...agent.institutionId, institutionId]
            : [institutionId];

          await client.models.Agent.update(
            { agentId, institutionId: updatedAgentInstitutionIds },
            { authMode: "userPool", authToken: token },
          );
        }
      }

      await client.models.Institution.update(
        {
          institutionId,
          institutionName: institution.institutionName,
          agentId: [...currentAgentIds, ...newAgentIds],
        },
        { authMode: "userPool", authToken: token },
      );

      return { success: true };
    } catch (error) {
      console.error("Error assigning agents to institution:", error);
      throw error;
    }
  }

  async removeAgentsFromInstitution(
    agentIds: string | string[],
    institutionId: string,
  ): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();
      const agentIdArray = Array.isArray(agentIds) ? agentIds : [agentIds];

      const { data: allInstitutions, errors } =
        await client.models.Institution.list({
          authMode: "userPool",
          authToken: token,
        });

      if (errors) {
        throw new Error("Failed to fetch institutions");
      }

      const institution = allInstitutions?.find(
        (inst) => inst.institutionId === institutionId,
      );

      if (!institution) {
        throw new Error("Institution not found");
      }

      for (const agentId of agentIdArray) {
        const { data: agent } = await client.models.Agent.get(
          { agentId },
          { authMode: "userPool", authToken: token },
        );

        if (agent?.institutionId) {
          await client.models.Agent.update(
            {
              agentId,
              institutionId: agent.institutionId.filter(
                (id) => id !== institutionId,
              ),
            },
            { authMode: "userPool", authToken: token },
          );
        }
      }

      const updatedInstitutionAgentIds = (institution.agentId || [])
        .filter((id): id is string => id !== null)
        .filter((id) => !agentIdArray.includes(id));

      await client.models.Institution.update(
        {
          institutionId,
          institutionName: institution.institutionName,
          agentId: updatedInstitutionAgentIds,
        },
        { authMode: "userPool", authToken: token },
      );

      return { success: true };
    } catch (error) {
      console.error("Error removing agents from institution:", error);
      throw error;
    }
  }

  async createInstitution(input: any): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      let domain: string;

      if (input.institutionId) {
        domain = this.extractDomain(input.institutionId);
      } else if (input.institutionEmail && input.institutionEmail.length > 0) {
        domain = this.extractDomain(input.institutionEmail[0]);
      } else {
        throw new Error(
          "Institution must have either an institutionId or email to generate domain",
        );
      }

      const { data, errors } = await client.models.Institution.create(
        {
          ...input,
          institutionId: domain,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        console.error("Error creating institution.");
        throw new Error("Failed to create institution");
      }

      return data;
    } catch (error) {
      console.error("Error creating institution:", error);
      throw error;
    }
  }

  async updateInstitution(id: string, input: Partial<any>): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      let institutionName = input.institutionName;

      if (!institutionName) {
        const existing = await this.getInstitutionById(id);
        institutionName = existing.institutionName;
      }

      const { data, errors } = await client.models.Institution.update(
        {
          institutionId: id,
          institutionName,
          ...input,
        },
        { authMode: "userPool", authToken: token },
      );

      if (errors) {
        console.error("Error updating institution.");
        throw new Error("Failed to update institution");
      }

      return data;
    } catch (error) {
      console.error("Error updating institution:", error);
      throw error;
    }
  }

  async deleteInstitution(id: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data: allInstitutions } = await client.models.Institution.list({
        authMode: "userPool",
        authToken: token,
      });

      const institution = allInstitutions?.find(
        (inst) => inst.institutionId === id,
      );

      if (!institution) {
        throw new Error("Institution not found");
      }

      if (institution?.agentId) {
        for (const agentId of institution.agentId) {
          if (!agentId) continue;

          const { data: agent } = await client.models.Agent.get(
            { agentId },
            { authMode: "userPool", authToken: token },
          );

          if (agent?.institutionId) {
            const updatedInstitutionIds = agent.institutionId.filter(
              (instId) => instId !== id && instId !== null,
            );

            await client.models.Agent.update(
              { agentId, institutionId: updatedInstitutionIds },
              { authMode: "userPool", authToken: token },
            );
          }
        }
      }

      const { data, errors } = await client.models.Institution.delete(
        { institutionId: id, institutionName: institution.institutionName },
        { authMode: "userPool", authToken: token },
      );

      if (errors) {
        console.error("Error deleting institution.");
        throw new Error("Failed to delete institution");
      }

      return data;
    } catch (error) {
      console.error("Error deleting institution:", error);
      throw error;
    }
  }
}

export const institutionManagementService = new InstitutionManagementService();
